/**
 * Admin API Client
 * All requests go through talkivo-worker with Supabase JWT auth.
 */

import { supabase } from "./supabase";

// Worker URL — injected by Webpack DefinePlugin
const ADMIN_API_URL = process.env.ADMIN_API_URL || "http://localhost:8787";

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

async function getAuthToken(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token || null;
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = await getAuthToken();
  if (!token) throw new Error("Not authenticated");

  const response = await fetch(`${ADMIN_API_URL}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  const result = (await response.json()) as ApiResponse<T>;
  if (!result.success) throw new Error(result.error || "Request failed");
  return result.data as T;
}

// ============================================
// Types
// ============================================

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  avatar_url: string | null;
  plan: string;
  credits_remaining: number;
  credits_total: number;
  bonus_credits: number;
  expiry_date: string | null;
  banned_until: string | null;
  created_at: string;
}

export interface UserListResponse {
  users: AdminUser[];
  total: number;
  page: number;
  limit: number;
}

export interface StatsResponse {
  total: number;
  free: number;
  lite: number;
  plus: number;
}

// ============================================
// API functions
// ============================================

export async function getUsers(params: {
  page?: number;
  limit?: number;
  search?: string;
} = {}): Promise<UserListResponse> {
  const sp = new URLSearchParams();
  if (params.page) sp.set("page", String(params.page));
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.search) sp.set("search", params.search);
  const query = sp.toString();
  return request<UserListResponse>(`/api/users${query ? `?${query}` : ""}`);
}

export async function getUser(userId: string): Promise<AdminUser> {
  return request<AdminUser>(`/api/users/${userId}`);
}

export async function updateUser(
  userId: string,
  updates: {
    plan?: string;
    credits_remaining?: number;
    credits_total?: number;
    bonus_credits?: number;
    expiry_date?: string | null;
  }
): Promise<unknown> {
  return request(`/api/users/${userId}`, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
}

export async function getStats(): Promise<StatsResponse> {
  return request<StatsResponse>("/api/stats");
}

export async function deleteUser(userId: string): Promise<unknown> {
  return request(`/api/users/${userId}`, { method: "DELETE" });
}

export async function banUser(userId: string): Promise<unknown> {
  return request(`/api/users/${userId}/ban`, { method: "POST" });
}

export async function unbanUser(userId: string): Promise<unknown> {
  return request(`/api/users/${userId}/unban`, { method: "POST" });
}

export async function healthCheck(): Promise<{ status: string }> {
  const res = await fetch(`${ADMIN_API_URL}/health`);
  const result = (await res.json()) as ApiResponse<{ status: string }>;
  return result.data || { status: "unknown" };
}
